import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { CheckCircle, Sparkles, ArrowRight, Loader2, Crown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const FeaturedMembers = ({ 
  limit = 6,
  className = ""
}) => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMembers();
  }, [user]);

  const fetchMembers = async () => {
    try {
      const params = { limit };
      if (user?.id) params.exclude_user_id = user.id;

      const response = await axios.get(`${API}/api/promo-pages/featured`, { params });
      setMembers(response.data);
    } catch (error) {
      console.error('Failed to load featured members:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex justify-center py-8 ${className}`}>
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (members.length === 0) {
    return null;
  }

  return (
    <section className={className} data-testid="featured-members-section">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
            <Crown className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-heading font-medium">Featured Members</h2>
            <p className="text-sm text-muted-foreground">Professional services from our community</p>
          </div>
        </div>
        <Link to="/services">
          <Button variant="ghost" size="sm">
            View All
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>

      {/* Members Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map((member) => (
          <Link 
            key={member.id}
            to={`/members/${member.username}`}
          >
            <Card className="h-full hover:shadow-md transition-shadow card-interactive">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Logo/Avatar */}
                  {member.logo_url ? (
                    <img 
                      src={member.logo_url}
                      alt={member.display_name}
                      className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <Avatar className="w-14 h-14 flex-shrink-0">
                      <AvatarImage src={member.avatar_url} />
                      <AvatarFallback className="text-lg">
                        {member.display_name?.charAt(0) || member.username?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium truncate">{member.display_name}</h3>
                      {member.is_verified && (
                        <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                      {member.headline}
                    </p>

                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="secondary" 
                        className={`text-xs ${
                          member.membership_tier === 'premium' 
                            ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700'
                            : ''
                        }`}
                      >
                        {member.membership_tier === 'premium' ? 'Premium' : 'Pro'}
                      </Badge>
                      {member.professional_groups?.slice(0, 1).map((group) => (
                        <Badge key={group} variant="outline" className="text-xs capitalize">
                          {group.replace('_', ' ')}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Tagline if available */}
                {member.tagline && (
                  <p className="text-xs text-muted-foreground mt-3 pt-3 border-t italic line-clamp-1">
                    "{member.tagline}"
                  </p>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default FeaturedMembers;
